import { SocketServerEvent } from "@constants/socket";
import { HubConnectionState } from "@microsoft/signalr";
import {
  DefaultSocketErrorMessage,
  ProcessResponse,
  ProductEditingProcessAction,
  ProductEditingProcessResult,
} from "@models/core";
import { RootState, useAppDispatch } from "@redux/store";
import { SignalRService } from "@services/signalr/signalRService";
import { useSelector } from "react-redux";
import { useNotification } from "./useNotification";
import {
  updateBeingKickedBy,
  updateEditMode,
  updateLoadingEditMode,
  updateShowBeingKickedDialog,
  updateShowClaimDeniedDialog,
  updateShowKickoutDialog,
  updateCurrentEditor,
} from "@redux/slices/product/investment-product";

export const useEditInvestmentProduct = () => {
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { notify } = useNotification();
  const signalrConnection = SignalRService.getHubConnection();

  const checkEditInvestmentProduct = async (
    productId: string,
    onSuccess?: () => void,
  ) => {
    dispatch(updateLoadingEditMode(true));
    if (
      signalrConnection &&
      signalrConnection.state === HubConnectionState.Connected
    ) {
      const checkingResponse: ProcessResponse<ProductEditingProcessResult> =
        await signalrConnection.invoke(
          SocketServerEvent.onProductEditorProcess,
          ProductEditingProcessAction.ProductEditorChecking,
          productId,
          null,
        );

      if (checkingResponse) {
        switch (checkingResponse.resultType) {
          case ProductEditingProcessResult.EditingPermissionDenied:
            dispatch(updateShowClaimDeniedDialog(true));
            break;
          case ProductEditingProcessResult.EditingPermissionClaimConfirm:
            dispatch(updateShowKickoutDialog(true));
            dispatch(updateCurrentEditor(checkingResponse.content));
            break;
          case ProductEditingProcessResult.EditingPermissionAvailable:
            await handleOnClaimingEditingPermission(productId, onSuccess);
            break;
        }
        dispatch(updateLoadingEditMode(false));
      }
    } else {
      dispatch(updateLoadingEditMode(false));
      notify(DefaultSocketErrorMessage, {
        variant: "error",
        close: true,
      })
    }
  };

  const handleOnClaimingEditingPermission = async (
    productId: string,
    onSuccess?: () => void
  ) => {
    if (signalrConnection) {
      const claimEditingResponse: ProcessResponse<ProductEditingProcessResult> =
        await signalrConnection.invoke(
          SocketServerEvent.onProductEditorProcess,
          ProductEditingProcessAction.ProductEditorClaiming,
          productId,
          user?.name,
        );

      switch (claimEditingResponse.resultType) {
        case ProductEditingProcessResult.EditingPermissionClaimFail:
          break;
        case ProductEditingProcessResult.EditingPermissionDenied:
          dispatch(updateShowClaimDeniedDialog(true));
          break;
        case ProductEditingProcessResult.EditingPermissionClaimSuccessful:
          dispatch(updateEditMode(true));
          signalrConnection.on(
            SocketServerEvent.onProductEditingSectionManagement,
            handleOnProductEditingSectionManagement,
          );
          if (typeof onSuccess === "function") {
            onSuccess();
          }
          break;
      }
    }
  };

  const handleOnProductEditingSectionManagement = (
    result: ProcessResponse<ProductEditingProcessResult>,
    productId: string,
  ) => {
    if (
      result &&
      result.resultType === ProductEditingProcessResult.EditingPermissionBeingDisposed
    ) {
      dispatch(updateEditMode(false));
      dispatch(updateBeingKickedBy(result.content));
      dispatch(updateShowBeingKickedDialog(true));

      if (signalrConnection) {
        signalrConnection.invoke(
          SocketServerEvent.onProductEditorProcess,
          ProductEditingProcessAction.ProductEditingBeingDisposed,
          productId,
          null,
        );
      }

      signalrConnection?.off(
        SocketServerEvent.onProductEditingSectionManagement,
      );
    }
  };

  const handleOnReleaseLockProductEditing = async (productId: string) => {
    if (
      signalrConnection &&
      signalrConnection.state === HubConnectionState.Connected
    ) {
      const releaseLockResponse: ProcessResponse<ProductEditingProcessResult> =
        await signalrConnection.invoke(
          SocketServerEvent.onProductEditorProcess,
          ProductEditingProcessAction.ProductEditingReleaseLock,
          productId,
          null,
        );

      if (releaseLockResponse) {
        signalrConnection.off(
          SocketServerEvent.onProductEditingSectionManagement,
        );
      }
    } else {
      notify(DefaultSocketErrorMessage, {
        variant: "error",
        close: true,
      });
    }
  };
  return {
    checkEditInvestmentProduct,
    handleOnClaimingEditingPermission,
    handleOnProductEditingSectionManagement,
    handleOnReleaseLockProductEditing,
  };
}
